import {
  IFarmInputCreditNote,
  IFarmInputInvoice,
  IFarmInputOrder,
} from 'constants/interfaces';
import React, {Fragment, useEffect, useReducer} from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import {_NumberWithSpaces} from 'constants/index';
import {Skeleton} from '@material-ui/lab';
import {
  LineItemState,
  creditNoteEditTableStateReducer,
} from 'screens/Dashboard/Invoices/CreditNote/CreditNoteEditTableState';

export function CreditNoteEditTable(props: {
  invoice?: IFarmInputInvoice;
  creditNote?: IFarmInputCreditNote;
  isLoading?: boolean;
  onSubmit: (lineItems: LineItemState[]) => any;
  isSubmitting: boolean;
}) {
  if (props.isLoading)
    return <Skeleton variant="rect" height={300} style={{borderRadius: 8}} />;
  if (!props.invoice) return null;
  return (
    <CreditNoteTable
      invoice={props.invoice}
      creditNote={props.creditNote}
      onSubmit={props.onSubmit}
      isSubmitting={props.isSubmitting}
    />
  );
}

function CreditNoteTable(props: {
  invoice: IFarmInputInvoice;
  creditNote?: IFarmInputCreditNote;
  onSubmit: (lineItems: LineItemState[]) => any;
  isSubmitting: boolean;
}) {
  const [state, dispatch] = useReducer(creditNoteEditTableStateReducer, {
    lineItems: [],
  });

  useEffect(() => {
    dispatch({type: 'SetInvoice', data: {invoice: props.invoice}});
    if (props.creditNote)
      dispatch({type: 'SetCreditNote', data: {creditNote: props.creditNote}});
  }, [props.invoice, props.creditNote]);

  const getTotal = () => {
    return state.lineItems
      .filter((item) => item.total)
      .reduce((total, curr) => total + curr.total!, 0);
  };

  const getLineItemState = (order: IFarmInputOrder) => {
    return state.lineItems.find((item) => item.order.id === order.id);
  };

  const isError = state.lineItems.some(
    (item) => item.priceError || item.qtyError,
  );

  return (
    <>
      <Card variant="outlined" className="credit-note-table-container">
        <TableContainer className="credit-note-table">
          <Table
            className="credit-note-table"
            aria-label="credit-note table"
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Price&nbsp;(R)</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Total&nbsp;(R)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.invoice.orders.map((order: IFarmInputOrder) => {
                const lineItemState = getLineItemState(order);
                return (
                  <Fragment key={order.id}>
                    <InvoiceLineItemRow lineItem={order} />
                    <CreditNoteLineItemRow
                      state={lineItemState}
                      onPriceChange={(price) =>
                        dispatch({type: 'UpdatePrice', data: {order, price}})
                      }
                      onQtyChange={(qty) =>
                        dispatch({type: 'UpdateQty', data: {order, qty}})
                      }
                    />
                  </Fragment>
                );
              })}
              <TableRow>
                <TableCell />
                <TableCell />
                <TableCell colSpan={2} className="total" align="right">
                  {_NumberWithSpaces(getTotal())}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box flex={1} />
        <Box display={'flex'} justifyContent={'center'} padding={2}>
          <Button
            variant={'contained'}
            size={'small'}
            color={'primary'}
            onClick={() => props.onSubmit(state.lineItems)}
            disabled={isError || !getTotal() || props.isSubmitting}
          >
            {props.isSubmitting ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>
      </Card>
    </>
  );
}

function InvoiceLineItemRow(props: {lineItem: IFarmInputOrder}) {
  const {lineItem} = props;
  return (
    <TableRow className="credit-note-table-row">
      <TableCell className="product-name">
        <Typography variant="body2" color="textSecondary">
          {lineItem.product.name}
        </Typography>
      </TableCell>
      <TableCell className="price" align="right">
        <Typography variant="body2" color="textSecondary">
          {_NumberWithSpaces(lineItem.price)}
        </Typography>
      </TableCell>
      <TableCell className="qty" align="right">
        <Typography variant="body2" color="textSecondary">
          {lineItem.quantity}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="textSecondary">
          {/* {_NumberWithSpaces(lineItem.price * lineItem.quantity)}*/}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function CreditNoteLineItemRow(props: {
  state?: LineItemState;
  onPriceChange: (price: string) => any;
  onQtyChange: (qty: string) => any;
}) {
  const {state, onPriceChange, onQtyChange} = props;
  return (
    <TableRow className="credit-note-table-row">
      <TableCell className="product-name">
        <Typography variant="body2" color="textSecondary">
          {/* {lineItem.product.name}*/}
        </Typography>
      </TableCell>
      <TableCell className="price" align="right">
        <Typography variant="body2" color="textSecondary">
          <TextField
            size="small"
            value={state?.price || ''}
            type="text"
            InputProps={{inputProps: {min: 0}}}
            variant="outlined"
            onChange={(event) => onPriceChange(event.target.value)}
            error={state?.priceError}
          />
        </Typography>
      </TableCell>
      <TableCell className="qty" align="right">
        <Typography variant="body2" color="textSecondary">
          <TextField
            size="small"
            value={state?.qty || ''}
            type="text"
            InputProps={{inputProps: {min: 0}}}
            variant="outlined"
            onChange={(event) => onQtyChange(event.target.value)}
            error={state?.qtyError}
          />
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="textSecondary">
          {state?.total ? _NumberWithSpaces(state?.total) : undefined}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
